import { AfterViewInit, Directive, ElementRef, OnDestroy, inject } from '@angular/core';

/**
 * Faz a lista do `po-combo` aparecer (PO-UI 21.15).
 *
 * O componente aplica o atributo `hidden` no DIV interno do listbox:
 *
 *     <po-combo>
 *       <div class="po-combo-container">        <- controla abrir/fechar
 *         <po-listbox p-type="option">          <- elemento, SEM hidden
 *           <div class="po-listbox" hidden>     <- o hidden fica aqui
 *             <ul class="po-listbox-list"> ... 50 <li> ...
 *
 * Com o `hidden`, a regra padrão do navegador (`[hidden] { display: none }`)
 * colapsa o div; o container herda altura zero e a lista some, mesmo com os
 * itens todos presentes no DOM.
 *
 * A biblioteca traz a regra que deveria neutralizar isso, mas ela mira o
 * ELEMENTO e não a classe, então não alcança o div:
 *
 *     po-listbox[hidden] { display: flex !important }
 *
 * Reescrever a regra para `.po-listbox[hidden]` também não resolveu — medido no
 * navegador, o `display` continuou `none` mesmo com `!important`. Remover o
 * atributo, medido no mesmo lugar, devolveu `display: flex` e altura 249px.
 * Por isso a correção é por atributo, e não por folha de estilo.
 *
 * Seguro porque quem governa abrir e fechar é o `.po-combo-container`: com o
 * combo fechado, ele fica `display: none` e nada aparece.
 *
 * O seletor é o próprio `po-combo`, então basta importar a diretiva no
 * componente que usa o combo — não há nada a acrescentar no template.
 */
@Directive({
  selector: 'po-combo',
  standalone: true
})
export class PoComboListaVisivelDirective implements AfterViewInit, OnDestroy {
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private observador?: MutationObserver;

  ngAfterViewInit(): void {
    this.removerHidden();

    // O listbox é criado e recriado a cada abertura e a cada nova busca, então
    // não basta limpar uma vez.
    this.observador = new MutationObserver(() => this.removerHidden());
    this.observador.observe(this.host.nativeElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['hidden']
    });
  }

  ngOnDestroy(): void {
    this.observador?.disconnect();
  }

  private removerHidden(): void {
    this.host.nativeElement
      .querySelectorAll<HTMLElement>('.po-listbox[hidden]')
      .forEach((el: HTMLElement) => el.removeAttribute('hidden'));
  }
}
